import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/lib/log";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { LogOut, LayoutDashboard, ArrowLeftRight, Target, Wallet, Receipt, FileText, Sparkles, Repeat, Crown, MailWarning, Scale, BookOpen, Tags } from "lucide-react";
import { VoiceExpenseWidget } from "@/components/VoiceExpense";
import { NotificationBell } from "@/components/NotificationBell";

const nav = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions" },
  { to: "/budgets", label: "Budgets", icon: Target, testid: "nav-budgets" },
  { to: "/accounts", label: "Accounts", icon: Wallet, testid: "nav-accounts" },
  { to: "/bills", label: "Bills", icon: Receipt, testid: "nav-bills" },
  { to: "/recurring", label: "Recurring", icon: Repeat, testid: "nav-recurring" },
  { to: "/categories", label: "Categories", icon: Tags, testid: "nav-categories" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/statements", label: "Statements", icon: Scale, testid: "nav-statements" },
  { to: "/journal", label: "Journal", icon: BookOpen, testid: "nav-journal" },
  { to: "/ask", label: "Ask AI", icon: Sparkles, testid: "nav-ask" },
  { to: "/pricing", label: "Pricing", icon: Crown, testid: "nav-pricing" },
];

function PlanBadge({ user }) {
  const plan = user?.effective_plan || "free";
  if (plan === "lifetime") {
    return (
      <NavLink to="/pricing" data-testid="plan-badge" className="hidden sm:flex items-center gap-1.5 border border-[#A67C00] text-[#A67C00] px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#A67C00] hover:text-white transition-colors">
        <Crown className="w-3 h-3" /> Lifetime
      </NavLink>
    );
  }
  if (plan === "premium") {
    const trialDays = user?.trial && user?.trial_days_left != null ? user.trial_days_left : null;
    return (
      <NavLink to="/pricing" data-testid="plan-badge" title={`Valid until ${formatDate(user.premium_until)}`} className="hidden sm:flex items-center gap-1.5 border border-[#A67C00] text-[#A67C00] px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#A67C00] hover:text-white transition-colors">
        <Crown className="w-3 h-3" />
        {trialDays != null
          ? `Trial · ${trialDays} day${trialDays === 1 ? "" : "s"} left`
          : `Premium · ${formatDate(user.premium_until)}`}
      </NavLink>
    );
  }
  return (
    <NavLink to="/pricing" data-testid="plan-badge" className="hidden sm:flex items-center gap-1.5 bg-[#A67C00] text-white px-3 py-2 text-[10px] uppercase tracking-widest hover:bg-[#8a6700] transition-colors">
      <Crown className="w-3 h-3" /> Upgrade
    </NavLink>
  );
}

function VerifyEmailBanner({ user }) {
  if (!user || user.email_verified !== false || user.provider === "google") return null;
  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
    } catch (e) {
      logError("Resend verification failed", e);
      toast.error("Could not send verification email");
    }
  };
  return (
    <div className="bg-[#A67C00] text-white" data-testid="verify-email-banner">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2 flex items-center justify-between gap-4 flex-wrap">
        <span className="flex items-center gap-2 text-xs tracking-wide">
          <MailWarning className="w-4 h-4" /> Please verify your email address to secure your account.
        </span>
        <button onClick={resend} data-testid="resend-verification-btn" className="border border-white px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-white hover:text-[#A67C00] transition-colors">
          Resend email
        </button>
      </div>
    </div>
  );
}

function HeaderActions({ user, onLogout }) {
  return (
    <div className="flex items-center gap-4">
      <PlanBadge user={user} />
      <NotificationBell />
      <div className="hidden sm:flex flex-col items-end">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565]">Account</span>
        <span className="font-sans text-sm" data-testid="header-user-name">{user?.name}</span>
      </div>
      <button
        data-testid="logout-btn"
        onClick={onLogout}
        className="flex items-center gap-2 px-3 py-2 border border-[#1A1F16] text-[#1A1F16] hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors text-xs uppercase tracking-widest"
      >
        <LogOut className="w-3.5 h-3.5" /> Sign out
      </button>
    </div>
  );
}

function NavStrip() {
  return (
    <nav className="border-t border-[#D8D4C7] bg-[#F7F5F0]/95">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex overflow-x-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? "border-[#1A1F16] text-[#1A1F16]"
                  : "border-transparent text-[#6C7565] hover:text-[#1A1F16]"
              }`
            }
          >
            <n.icon className="w-3.5 h-3.5" /> {n.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1A1F16]">
      {/* Top ledger header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-[#D8D4C7]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Est. 2026 · INR Ledger</span>
          </div>
          <div className="flex items-baseline gap-6">
            <h1 className="font-serif text-3xl lg:text-4xl tracking-tight" data-testid="brand-title">
              Fin<span className="italic text-[#A67C00]">·</span>Ledger
            </h1>
          </div>
          <HeaderActions user={user} onLogout={handleLogout} />
        </div>
        <NavStrip />
      </header>

      <VerifyEmailBanner user={user} />

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 fade-in">
        {children ?? <Outlet />}
      </main>

      <VoiceExpenseWidget />

      <footer className="border-t border-[#D8D4C7] mt-16 py-6 text-center font-mono text-[10px] tracking-widest uppercase text-[#6C7565]">
        FinLedger — A household ledger · Tracked in ₹
      </footer>
    </div>
  );
}
